import { useCallback, useEffect, useRef, useState } from 'react'

export type AutosaveStatus = 'idle' | 'saving' | 'saved' | 'error'

// Shared debounce-and-status autosave for plain-text note/remark fields (never money or status
// fields -- those keep an explicit Save on purpose). `save` must throw on failure so the error
// state shows; the typed text is never cleared, so the user can just retry.
export function useAutosaveText(
  initial: string,
  save: (value: string) => Promise<void>,
  { delayMs = 500, fadeMs = 2000 }: { delayMs?: number; fadeMs?: number } = {},
) {
  const [value, setValue] = useState(initial)
  const [status, setStatus] = useState<AutosaveStatus>('idle')
  const debounceRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)
  const fadeRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)
  const saveRef = useRef(save)
  saveRef.current = save

  useEffect(() => () => {
    clearTimeout(debounceRef.current)
    clearTimeout(fadeRef.current)
  }, [])

  const run = useCallback(async (v: string) => {
    setStatus('saving')
    try {
      await saveRef.current(v)
      setStatus('saved')
      clearTimeout(fadeRef.current)
      fadeRef.current = setTimeout(() => setStatus('idle'), fadeMs)
    } catch {
      setStatus('error')
    }
  }, [fadeMs])

  const onChange = useCallback((v: string) => {
    setValue(v)
    setStatus('idle')
    clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => run(v), delayMs)
  }, [run, delayMs])

  // For the optional Save button: skip the debounce wait and save now.
  const saveNow = useCallback(() => {
    clearTimeout(debounceRef.current)
    return run(value)
  }, [run, value])

  // For async-loaded records: seed the value without triggering a save.
  const reset = useCallback((v: string) => {
    clearTimeout(debounceRef.current)
    setValue(v)
    setStatus('idle')
  }, [])

  return { value, onChange, status, saveNow, reset }
}
